import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

// Interfaces
export interface Restaurant {
  id: string;
  name?: string;
  roles?: string[];
}

export interface AuthState {
  isAuthenticated: boolean;
  username: string | null;
  restaurants: Restaurant[];
  currentRestaurant: Restaurant | null;
  roles: string[];
}

interface AuthContextValue {
  authState: AuthState;
  login: (username: string, password: string, restaurants: Restaurant[]) => void;
  logout: () => void;
  selectRestaurant: (restaurantId: string) => void;
  hasMultipleRestaurants: () => boolean;
  isAdmin: () => boolean;
  isManager: () => boolean;
  hasManagerAccess: () => boolean;
}

// Session storage keys
const STORAGE_KEYS = {
  USERNAME: "username",
  PASSWORD: "password",
  RESTAURANT_ID: "restaurant_id",
  RESTAURANTS: "ticketrails_restaurants",
  ROLES: "ticketrails_roles",
  LOGGED_IN: "ticketrails_logged_in",
} as const;

// Initial state
const initialAuthState: AuthState = {
  isAuthenticated: false,
  username: null,
  restaurants: [],
  currentRestaurant: null,
  roles: [],
};

// Create context
const AuthContext = createContext<AuthContextValue | null>(null);

// Helper to load initial state from sessionStorage
const loadStateFromStorage = (): AuthState => {
  const isLoggedIn = sessionStorage.getItem(STORAGE_KEYS.LOGGED_IN) === "true";
  const username = sessionStorage.getItem(STORAGE_KEYS.USERNAME);
  const restaurantId = sessionStorage.getItem(STORAGE_KEYS.RESTAURANT_ID);
  const rolesCSV = sessionStorage.getItem(STORAGE_KEYS.ROLES);
  const restaurantsJSON = sessionStorage.getItem(STORAGE_KEYS.RESTAURANTS);

  if (!isLoggedIn || !username) {
    return initialAuthState;
  }

  const roles = rolesCSV ? rolesCSV.split(",") : [];
  let restaurants: Restaurant[] = [];

  try {
    restaurants = restaurantsJSON ? JSON.parse(restaurantsJSON) : [];
  } catch {
    restaurants = [];
  }

  const currentRestaurant = restaurantId
    ? restaurants.find((r) => r.id === restaurantId) || { id: restaurantId, roles }
    : null;

  return {
    isAuthenticated: true,
    username,
    restaurants,
    currentRestaurant,
    roles,
  };
};

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(loadStateFromStorage);

  // Sync state to sessionStorage whenever it changes
  useEffect(() => {
    if (authState.isAuthenticated) {
      sessionStorage.setItem(STORAGE_KEYS.LOGGED_IN, "true");
      if (authState.username) {
        sessionStorage.setItem(STORAGE_KEYS.USERNAME, authState.username);
      }
      if (authState.restaurants.length > 0) {
        sessionStorage.setItem(STORAGE_KEYS.RESTAURANTS, JSON.stringify(authState.restaurants));
      }
      if (authState.currentRestaurant) {
        sessionStorage.setItem(STORAGE_KEYS.RESTAURANT_ID, authState.currentRestaurant.id);
      }
      if (authState.roles.length > 0) {
        sessionStorage.setItem(STORAGE_KEYS.ROLES, authState.roles.join(","));
      }
    }
  }, [authState]);

  const login = useCallback((username: string, password: string, restaurants: Restaurant[]) => {
    // Store credentials in sessionStorage for Basic Auth API calls
    sessionStorage.setItem(STORAGE_KEYS.USERNAME, username);
    sessionStorage.setItem(STORAGE_KEYS.PASSWORD, password);
    sessionStorage.setItem(STORAGE_KEYS.LOGGED_IN, "true");
    sessionStorage.setItem(STORAGE_KEYS.RESTAURANTS, JSON.stringify(restaurants));

    setAuthState({
      isAuthenticated: true,
      username,
      restaurants,
      currentRestaurant: null,
      roles: [],
    });
  }, []);

  const logout = useCallback(() => {
    // Clear all auth data from sessionStorage
    sessionStorage.removeItem(STORAGE_KEYS.USERNAME);
    sessionStorage.removeItem(STORAGE_KEYS.PASSWORD);
    sessionStorage.removeItem(STORAGE_KEYS.RESTAURANT_ID);
    sessionStorage.removeItem(STORAGE_KEYS.RESTAURANTS);
    sessionStorage.removeItem(STORAGE_KEYS.ROLES);
    sessionStorage.removeItem(STORAGE_KEYS.LOGGED_IN);

    setAuthState(initialAuthState);
  }, []);

  const selectRestaurant = useCallback((restaurantId: string) => {
    setAuthState((prev) => {
      const restaurant = prev.restaurants.find((r) => r.id === restaurantId);
      if (!restaurant) {
        console.error(`Restaurant with id ${restaurantId} not found`);
        return prev;
      }

      const roles = restaurant.roles || [];

      // Sync to sessionStorage
      sessionStorage.setItem(STORAGE_KEYS.RESTAURANT_ID, restaurant.id);
      sessionStorage.setItem(STORAGE_KEYS.ROLES, roles.join(","));

      return {
        ...prev,
        currentRestaurant: restaurant,
        roles,
      };
    });
  }, []);

  const hasMultipleRestaurants = useCallback(() => {
    return authState.restaurants.length > 1;
  }, [authState.restaurants.length]);

  const isAdmin = useCallback(() => {
    return authState.roles.includes("Admin");
  }, [authState.roles]);

  const isManager = useCallback(() => {
    return authState.roles.includes("Manager");
  }, [authState.roles]);

  const hasManagerAccess = useCallback(() => {
    return authState.roles.includes("Admin") || authState.roles.includes("Manager");
  }, [authState.roles]);

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      authState,
      login,
      logout,
      selectRestaurant,
      hasMultipleRestaurants,
      isAdmin,
      isManager,
      hasManagerAccess,
    }),
    [
      authState,
      login,
      logout,
      selectRestaurant,
      hasMultipleRestaurants,
      isAdmin,
      isManager,
      hasManagerAccess,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
